import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import {
  LayoutGrid,
  Bot,
  Wrench,
  ClipboardList,
  ShieldCheck,
  ChevronsUpDown,
  LogOut,
  Moon,
} from 'lucide-react';
import { config } from '../config/env';
import { getAgentOwnership } from '../lib/api-client';
import { getStoredTheme, setTheme } from '../lib/theme';

const initialsFor = (name: string) =>
  name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

export const AppShell = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isDark, setIsDark] = useState(() => getStoredTheme() === 'dark');
  const userRef = useRef<HTMLDivElement>(null);

  const handleToggleDark = () => {
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    setIsDark(next === 'dark');
  };

  const logoUrl = `${import.meta.env.BASE_URL}${isDark ? 'dark_logo.png' : 'logo.png'}`;

  const userName = (authState?.idToken?.claims?.name ||
    authState?.idToken?.claims?.email) as string | undefined;

  useEffect(() => {
    let cancelled = false;
    getAgentOwnership()
      .then((response) => {
        if (!cancelled) {
          setIsOwner(response.agents.length > 0);
        }
      })
      .catch(() => {
        // Ownership lookup is best-effort for nav visibility only — if the
        // governance API isn't reachable/configured, just hide the item
        // rather than surface an error on every page load.
        if (!cancelled) {
          setIsOwner(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const navItems = [
    { to: '/', label: 'Overview', icon: LayoutGrid, end: true },
    { to: '/agents', label: 'My Agents', icon: Bot },
    { to: '/tools', label: 'My Tools', icon: Wrench },
    { to: '/request-access', label: 'Request Access', icon: ClipboardList },
    ...(isOwner ? [{ to: '/owners', label: 'Agent Owners', icon: ShieldCheck }] : []),
  ];

  return (
    <div className="app-frame">
      <header className="top-banner">
        <img src={logoUrl} alt="" className="brand-logo" />
        {config.appName}
      </header>

      <div className="app-shell">
        <aside className="sidebar">
          <ul className="sidebar-nav">
            {navItems.map(({ to, label, icon: Icon, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) => `sidebar-nav-item${isActive ? ' active' : ''}`}
                >
                  <Icon size={16} />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>

          {userName ? (
            <div className="sidebar-user" ref={userRef}>
              <button
                type="button"
                className="sidebar-user-button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-expanded={menuOpen}
              >
                <span className="sidebar-user-avatar">{initialsFor(userName)}</span>
                <span className="sidebar-user-name">{userName}</span>
                <ChevronsUpDown size={14} className="muted" />
              </button>
              {menuOpen ? (
                <div className="sidebar-user-menu">
                  <button type="button" className="sidebar-user-menu-row" onClick={handleToggleDark}>
                    <span>
                      <Moon size={14} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
                      Dark Mode
                    </span>
                    <span className={`toggle-switch${isDark ? ' on' : ''}`} role="switch" aria-checked={isDark}>
                      <span className="toggle-knob" />
                    </span>
                  </button>
                  <button type="button" onClick={() => oktaAuth.signOut()}>
                    <LogOut size={14} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </aside>

        <main className="main">
          <div className="main-inner">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
